import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOAN_STAGES } from "@/components/StagePill";
import { toast } from "sonner";

// Compact stage set for the visual board (mirrors LOAN_STAGES from StagePill)
const STAGES = LOAN_STAGES;

const CAN_EDIT_ROLES = new Set(["admin", "loan_rep"]);

function Card({ app, canEdit, onDragStart, isDragging }) {
  return (
    <Link
      to={`/app/applications/${app.id}`}
      draggable={canEdit}
      onDragStart={(e) => onDragStart?.(e, app)}
      data-testid={`kanban-card-${app.id}`}
      className={`block bg-white border border-slate-200 px-3 py-3 mb-2 ${
        canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
      } hover:border-slate-800 transition-colors ${isDragging ? "opacity-40" : "opacity-100"}`}
    >
      <div className="font-mono text-[10px] text-slate-500 truncate">{app.loan_number}</div>
      <div className="mt-1 font-semibold text-[13px] text-[#1F2937] leading-snug line-clamp-2">
        {app.product_name}
      </div>
      <div className="mt-2 text-[12px] text-slate-600 truncate">{app.borrower_name || "—"}</div>
      <div className="mt-1 font-mono text-[11px] text-[#1F2937]">{fmtMoney(app.requested_amount)}</div>
    </Link>
  );
}

export default function KanbanBoard({ title = "Pipeline", subtitle = "Drag files across positions" }) {
  const { user } = useAuth();
  const canEdit = CAN_EDIT_ROLES.has(user?.role);
  const [apps, setApps] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const load = () => {
    setLoading(true);
    api
      .get("/applications")
      .then((r) => setApps(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const onDragStart = (e, app) => {
    setDragging(app);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", app.id); } catch { /* ignore */ }
  };

  const onDragEnd = () => {
    setDragging(null);
    setDragOver(null);
  };

  const onDragOver = (e, stage) => {
    if (!canEdit) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOver !== stage) setDragOver(stage);
  };

  const onDrop = async (e, stage) => {
    e.preventDefault();
    setDragOver(null);
    if (!canEdit || !dragging) return;
    const app = dragging;
    setDragging(null);
    if (app.stage === stage) return;

    // optimistic update
    const prev = apps;
    setApps((cur) => cur.map((a) => (a.id === app.id ? { ...a, stage } : a)));
    try {
      await api.patch(`/applications/${app.id}/stage`, { stage });
      toast.success(`Moved to ${stage}`);
    } catch (err) {
      setApps(prev);
      toast.error(err?.response?.data?.detail || "Could not update stage");
    }
  };

  const grouped = STAGES.reduce((acc, s) => {
    acc[s] = apps.filter((a) => a.stage === s);
    return acc;
  }, {});

  return (
    <section data-testid="kanban-board">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="small-caps text-slate-500">{title}</div>
          <h2 className="mt-3 font-semibold text-[32px] text-[#1F2937]">
            {canEdit ? subtitle : "Files by position"}
          </h2>
        </div>
        {canEdit && (
          <div className="small-caps text-slate-500 text-[10px]" data-testid="kanban-hint">
            Drag a card to change its position
          </div>
        )}
      </div>

      <div
        ref={scrollRef}
        className="mt-8 flex gap-3 overflow-x-auto pb-4 -mx-1 px-1"
        data-testid="kanban-scroll"
      >
        {STAGES.map((stage) => {
          const items = grouped[stage] || [];
          const isOver = dragOver === stage;
          return (
            <div
              key={stage}
              className={`shrink-0 w-[240px] border ${
                isOver ? "border-[#1F2937] bg-slate-50" : "border-slate-200 bg-slate-50/40"
              } transition-colors`}
              onDragOver={(e) => onDragOver(e, stage)}
              onDragLeave={() => setDragOver((cur) => (cur === stage ? null : cur))}
              onDrop={(e) => onDrop(e, stage)}
              data-testid={`kanban-column-${stage.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="px-3 py-3 border-b border-slate-200 flex items-baseline justify-between">
                <div className="small-caps text-[#1F2937] text-[10px]">{stage}</div>
                <div className="font-mono text-[11px] text-slate-500" data-testid={`kanban-count-${stage.toLowerCase().replace(/\s+/g, "-")}`}>
                  {items.length}
                </div>
              </div>
              <div
                className="p-2 min-h-[120px]"
                onDragEnd={onDragEnd}
              >
                {items.length === 0 ? (
                  <div className="py-8 text-center small-caps text-slate-400 text-[10px]">
                    {isOver ? "Drop here" : "Empty"}
                  </div>
                ) : (
                  items.map((app) => (
                    <Card
                      key={app.id}
                      app={app}
                      canEdit={canEdit}
                      onDragStart={onDragStart}
                      isDragging={dragging?.id === app.id}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!loading && apps.length === 0 && (
        <div className="py-16 text-center font-semibold text-[16px] text-slate-500" data-testid="kanban-empty">
          No files yet.
        </div>
      )}
    </section>
  );
}
